import {
  DimeAlert,
  DimeAlertDescription,
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ListErrorState } from '../../../../components/ListErrorState.js';
import { assertUuidRouteParams } from '../../../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../../../modules/iam/permissions-context.js';
import { LeafLinkProductImportWizard } from '../../../../modules/leaflink-companies/leaflink-product-import-wizard.js';
import { LeafLinkWebUrlCompanyContext } from '../../../../modules/leaflink-companies/leaflink-web-url-company-context.js';
import { toUsableLeafLinkCompanyBrands } from '../../../../modules/leaflink-companies/to-usable-leaflink-company-brands.js';
import { useLeafLinkCompanyQuery } from '../../../../modules/leaflink-companies/use-leaflink-company-query.js';

export const Route = createFileRoute('/integrations/leaflink/$leafLinkIntegrationId/products')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: LeafLinkProductsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkProductsPage() {
  const { leafLinkIntegrationId } = Route.useParams();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('leafLinkIntegration:edit');

  const {
    data: companyData,
    error: companyError,
    refetch: refetchCompany,
  } = useLeafLinkCompanyQuery(leafLinkIntegrationId);
  const company = companyData?.leafLinkCompany ?? null;

  const filterableBrands = useMemo(
    () => toUsableLeafLinkCompanyBrands(company?.leafLinkCompanyBrand ?? []),
    [company],
  );

  return (
    <LeafLinkWebUrlCompanyContext.Provider value={company}>
      <DimeCard borderless className="gap-0 py-0">
        <DimeCardHeader className="px-0 mb-4">
          <DimeCardTitle className="text-base">
            {company ? `${company.name} — Import LeafLink Products` : 'Import LeafLink Products'}
          </DimeCardTitle>
          <DimeCardDescription className="text-sm">
            Pull a brand's LeafLink product catalog into Nudool, assign categories, and import new
            products.
          </DimeCardDescription>
        </DimeCardHeader>
        {companyError ? (
          <ListErrorState
            title="Could not load this LeafLink company"
            message={companyError.message}
            onRetry={() => void refetchCompany()}
          />
        ) : (
          <>
            {company && !company.state && (
              <DimeAlert className="mb-4">
                <DimeAlertDescription>
                  This LeafLink company has no state, so its products can be browsed but not
                  imported.
                </DimeAlertDescription>
              </DimeAlert>
            )}
            <LeafLinkProductImportWizard
              leafLinkIntegrationId={leafLinkIntegrationId}
              stateId={company?.state?.id ?? ''}
              filterableBrands={filterableBrands}
              canEdit={canEdit}
            />
          </>
        )}
      </DimeCard>
    </LeafLinkWebUrlCompanyContext.Provider>
  );
}
